import React, { useState, useMemo, useCallback } from "react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Plus,
  Check,
  Clock,
  CalendarBlank,
  X,
  SpinnerGap,
  Warning,
  ChatText,
  PencilSimple,
  DownloadSimple,
} from "@phosphor-icons/react";
import {
  AssetMaintenanceSchedule,
  MaintenanceEvent,
  MaintenanceEventStatus,
  MaintenanceFrequencyLabels,
  MaintenanceType,
} from "shared/types/asset";
import { useScheduleEvents, useCreateAdhocEvent } from "hooks/useAssets";
import { toast } from "react-toastify";
import { MaintenanceEditDialog } from "./MaintenanceEditDialog";
import { MaintenanceValidationDialog } from "./MaintenanceValidationDialog";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useAuth } from "@clerk/clerk-react";

interface MaintenanceTimelineProps {
  schedules: AssetMaintenanceSchedule[];
  onRefetchSchedules: () => void;
  /** Render slot for a create-schedule button/dialog trigger */
  renderCreateButton?: () => React.ReactNode;
}

export const MaintenanceTimeline = ({
  schedules,
  onRefetchSchedules,
  renderCreateButton,
}: MaintenanceTimelineProps) => {
  if (schedules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <CalendarBlank className="h-8 w-8 mb-2" />
        <p className="text-sm">Geen onderhoudsschema's gepland</p>
        <p className="text-xs mt-1">
          Maak eerst een onderhoudsschema aan om onderhoudsdatums te loggen.
        </p>
        {renderCreateButton && <div className="mt-3">{renderCreateButton()}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header row: legend + create button */}
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
            Gevalideerd
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            Gerapporteerd
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
            Openstaand
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full border-2 border-orange-400 bg-white" />
            Volgend gepland
          </div>
        </div>
        {renderCreateButton && renderCreateButton()}
      </div>

      {schedules.map((schedule) => (
        <ScheduleTimeline
          key={schedule.id}
          schedule={schedule}
          onRefetchSchedules={onRefetchSchedules}
        />
      ))}
    </div>
  );
};

interface ScheduleTimelineProps {
  schedule: AssetMaintenanceSchedule;
  onRefetchSchedules: () => void;
}

const ScheduleTimeline = React.memo(({
  schedule,
  onRefetchSchedules,
}: ScheduleTimelineProps) => {
  const { events, isLoading, refetch } = useScheduleEvents(schedule.id);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [validatingSchedule, setValidatingSchedule] =
    useState<AssetMaintenanceSchedule | null>(null);
  const hasLoadedOnce = React.useRef(false);

  if (!isLoading) {
    hasLoadedOnce.current = true;
  }

  const sortedEvents = useMemo(() => {
    return [...events].sort(
      (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    );
  }, [events]);

  const handleEventAdded = useCallback(() => {
    refetch();
    onRefetchSchedules();
    setShowAddForm(false);
  }, [refetch, onRefetchSchedules]);

  const handleCancelForm = useCallback(() => {
    setShowAddForm(false);
  }, []);

  const toggleAddForm = useCallback(() => {
    setShowAddForm((prev) => !prev);
  }, []);

  const handleEditSaved = useCallback(() => {
    refetch();
    onRefetchSchedules();
    setEditOpen(false);
  }, [refetch, onRefetchSchedules]);

  const handleValidateSuccess = useCallback(() => {
    refetch();
    onRefetchSchedules();
    setValidatingSchedule(null);
  }, [refetch, onRefetchSchedules]);

  const handleValidate = useCallback(() => {
    setValidatingSchedule(schedule);
  }, [schedule]);

  const isCritical = schedule.maintenanceType === MaintenanceType.CRITICAL;
  const showInitialLoader = isLoading && !hasLoadedOnce.current;

  return (
    <div className="space-y-2">
      {/* Schedule header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-sm font-semibold truncate">{schedule.name}</h4>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
              isCritical
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isCritical ? "bg-red-500" : "bg-green-500"
              }`}
            />
            {isCritical ? "Kritiek" : "Preventief"}
          </span>
          <span className="text-xs text-muted-foreground">
            {MaintenanceFrequencyLabels[schedule.frequency]}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditOpen(true)}
            className="gap-1 h-7 text-xs"
            title="Schema bewerken"
          >
            <PencilSimple className="h-3 w-3" />
            Bewerken
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAddForm}
            className="gap-1 h-7 text-xs"
          >
            {showAddForm ? (
              <>
                <X className="h-3 w-3" />
                Annuleren
              </>
            ) : (
              <>
                <Plus className="h-3 w-3" />
                Datum toevoegen
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <AddEventForm
          scheduleId={schedule.id}
          onSuccess={handleEventAdded}
          onCancel={handleCancelForm}
        />
      )}

      {/* Timeline - only show full loader on initial load, never unmount content for background refetches */}
      {showInitialLoader ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-2">
          <SpinnerGap className="h-3.5 w-3.5 animate-spin" />
          Laden...
        </div>
      ) : (
        <TimelineList
          events={sortedEvents}
          nextDueDate={schedule.nextDueDate}
          onValidate={handleValidate}
        />
      )}

      {/* Edit schedule dialog */}
      <MaintenanceEditDialog
        schedule={schedule}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={handleEditSaved}
      />

      {/* Validation dialog */}
      {validatingSchedule && (
        <MaintenanceValidationDialog
          schedule={validatingSchedule}
          onClose={() => setValidatingSchedule(null)}
          onSuccess={handleValidateSuccess}
        />
      )}
    </div>
  );
}, (prev, next) => {
  // Only re-render if the schedule data actually changed
  return prev.schedule.id === next.schedule.id
    && prev.schedule.updatedAt === next.schedule.updatedAt
    && prev.schedule.nextDueDate === next.schedule.nextDueDate
    && prev.schedule.name === next.schedule.name
    && prev.schedule.frequency === next.schedule.frequency
    && prev.schedule.maintenanceType === next.schedule.maintenanceType
    && prev.onRefetchSchedules === next.onRefetchSchedules;
});

interface TimelineListProps {
  events: MaintenanceEvent[];
  nextDueDate?: string;
  onValidate?: () => void;
}

const TimelineList = ({ events, nextDueDate, onValidate }: TimelineListProps) => {
  const hasEvents = events.length > 0;
  const hasNextDue = !!nextDueDate;

  if (!hasEvents && !hasNextDue) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        Nog geen onderhoudsgebeurtenissen — voeg een datum toe om te starten.
      </p>
    );
  }

  return (
    <div className="relative ml-3">
      {/* Vertical line */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-border" />

      {events.map((event) => (
        <TimelineEventRow key={event.id} event={event} />
      ))}

      {hasNextDue && <NextDueRow date={nextDueDate} onValidate={onValidate} />}
    </div>
  );
};

const EVENT_STATUS_CONFIG: Record<
  MaintenanceEventStatus,
  { color: string; bgColor: string; label: string }
> = {
  [MaintenanceEventStatus.VALIDATED]: {
    color: "bg-green-500",
    bgColor: "bg-green-50 text-green-700 border-green-200",
    label: "Gevalideerd",
  },
  [MaintenanceEventStatus.REPORTED]: {
    color: "bg-blue-500",
    bgColor: "bg-blue-50 text-blue-700 border-blue-200",
    label: "Gerapporteerd",
  },
  [MaintenanceEventStatus.OPEN]: {
    color: "bg-gray-400",
    bgColor: "bg-gray-50 text-gray-600 border-gray-200",
    label: "Openstaand",
  },
};

const TimelineEventRow = ({ event }: { event: MaintenanceEvent }) => {
  const { getToken } = useAuth();
  const config = EVENT_STATUS_CONFIG[event.status];
  const dueDate = new Date(event.dueDate).toLocaleDateString("nl-BE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const completedDate = event.completedDate
    ? new Date(event.completedDate).toLocaleDateString("nl-BE", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  const handleDownloadReport = () => {
    // Prefer SharePoint URL (opens in browser/SharePoint viewer)
    if (event.sharePointFileUrl) {
      window.open(event.sharePointFileUrl, "_blank");
      return;
    }
    // Fallback: download from local storage via API
    downloadReportFromApi();
  };

  const downloadReportFromApi = async () => {
    try {
      const session = await getToken();
      const response = await fetchWithAuth(
        `${API_URL}/maintenance-reports/${event.id}/download`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error("Kon rapport niet downloaden");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers.get("Content-Disposition");
      const filename = disposition?.match(/filename="(.+)"/)?.[1] ?? "rapport.pdf";
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Kon rapport niet downloaden");
    }
  };

  return (
    <div className="relative pl-5 pb-4 last:pb-0">
      {/* Dot on the line */}
      <div
        className={`absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full ${config.color} -translate-x-[5px] ring-2 ring-white`}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{dueDate}</span>
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 h-4 ${config.bgColor}`}
            >
              {config.label}
            </Badge>
          </div>
          {completedDate && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Check className="h-3 w-3 text-green-600" />
              Voltooid op {completedDate}
            </p>
          )}
          {event.summary && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <ChatText className="h-3 w-3" />
              {event.summary}
            </p>
          )}
          {(event.reportFilePath || event.sharePointFileUrl) && (
            <button
              onClick={handleDownloadReport}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer"
            >
              <DownloadSimple className="h-3 w-3" />
              {event.sharePointFileUrl ? "Rapport openen" : "Rapport downloaden"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const NextDueRow = ({
  date,
  onValidate,
}: {
  date: string;
  onValidate?: () => void;
}) => {
  const isOverdue = new Date(date) < new Date();
  const formattedDate = new Date(date).toLocaleDateString("nl-BE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div className="relative pl-5">
      {/* Hollow dot on the line */}
      <div
        className={`absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full bg-white border-2 -translate-x-[5px] ring-2 ring-white ${
          isOverdue ? "border-red-500" : "border-orange-400"
        }`}
      />

      <div className="flex items-center gap-2">
        <span
          className={`text-sm font-medium ${
            isOverdue ? "text-red-600" : "text-muted-foreground"
          }`}
        >
          {formattedDate}
        </span>
        {isOverdue ? (
          <>
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
              <Warning className="h-3 w-3 mr-0.5" />
              Achterstallig
            </Badge>
            {onValidate && (
              <Button
                variant="outline"
                size="sm"
                onClick={onValidate}
                className="h-5 text-[10px] px-2 gap-0.5"
              >
                <Check className="h-3 w-3" />
                Valideer
              </Button>
            )}
          </>
        ) : (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 h-4 bg-orange-50 text-orange-700 border-orange-200"
          >
            <Clock className="h-3 w-3 mr-0.5" />
            Volgend gepland
          </Badge>
        )}
      </div>
    </div>
  );
};

interface AddEventFormProps {
  scheduleId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const AddEventForm = ({
  scheduleId,
  onSuccess,
  onCancel,
}: AddEventFormProps) => {
  const { createAdhocEvent, isCreating } = useCreateAdhocEvent();
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = async () => {
    if (!date) {
      toast.error("Datum is verplicht");
      return;
    }

    try {
      await createAdhocEvent(scheduleId, date, notes || undefined);
      toast.success("Onderhoudsgebeurtenis toegevoegd");
      onSuccess();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Kon gebeurtenis niet toevoegen";
      toast.error(message);
      console.error("Error creating adhoc event:", error);
    }
  };

  return (
    <div className="rounded-md border border-dashed p-3 bg-muted/30 space-y-3">
      <p className="text-xs font-medium">Onderhoudsdatum toevoegen</p>
      <div className="flex items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Datum</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-8 text-sm w-[160px]"
          />
        </div>
        <div className="flex-1 space-y-1">
          <Label className="text-xs">Notities (optioneel)</Label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Bijv. jaarlijkse keuring"
            className="h-8 text-sm"
          />
        </div>
        <div className="flex gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isCreating}
          >
            Annuleren
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={isCreating}>
            {isCreating ? (
              <SpinnerGap className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <Plus className="h-3.5 w-3.5 mr-1" />
            )}
            Toevoegen
          </Button>
        </div>
      </div>
    </div>
  );
};
